import { useState, type ReactNode } from "react";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOn from "@mui/icons-material/LocationOn";
import Person from "@mui/icons-material/Person";
import Phone from "@mui/icons-material/Phone";
import Psychology from "@mui/icons-material/Psychology";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import Schedule from "@mui/icons-material/Schedule";
import { Box, Collapse, Paper, Typography, useTheme } from "@mui/material";

import type { ExtendedThalamosUser } from "@thalamos/aspire-common";

import { ExpandMore } from "~/components/layout/styleWrappers.js";

export interface SetupArea {
  key: string;
  label: string;
  icon: ReactNode;
  complete: boolean;
  /** Section key to navigate to when clicked */
  sectionKey: string;
  /** Optional — area is a bonus and not counted towards the score */
  bonus?: boolean;
}

export interface SetupScoreCardsProps {
  areas: SetupArea[];
  onAreaClick?: (sectionKey: string) => void;
  /** Highlights the card whose sectionKey matches this value */
  selectedSection?: string;
}

/**
 * Builds scored setup areas from user profile data.
 * Returns an array of SetupArea objects for rendering.
 */
export function buildSetupAreas(opts: {
  hasPhoneNumber: boolean;
  hasWorkingHours: boolean;
  hasHomeBase: boolean;
  hasLaCoverage: boolean;
  hasSpecialisms: boolean;
  calendarSyncActive: boolean;
  hasAboutInfo: boolean;
}): SetupArea[] {
  return [
    {
      key: "working-hours",
      label: "Working Hours",
      icon: <Schedule fontSize="small" />,
      complete: opts.hasWorkingHours,
      sectionKey: "schedule",
    },
    {
      key: "location",
      label: "Location & Coverage",
      icon: <LocationOn fontSize="small" />,
      complete: opts.hasHomeBase && opts.hasLaCoverage,
      sectionKey: "location",
    },
    {
      key: "specialisms",
      label: "Specialisms",
      icon: <Psychology fontSize="small" />,
      complete: opts.hasSpecialisms,
      sectionKey: "specialisms",
    },
    {
      key: "about",
      label: "About",
      icon: <Person fontSize="small" />,
      complete: opts.hasAboutInfo,
      sectionKey: "about",
      bonus: true,
    },
    {
      key: "contact",
      label: "Phone Number",
      icon: <Phone fontSize="small" />,
      complete: opts.hasPhoneNumber,
      sectionKey: "contact",
      bonus: true,
    },
  ];
}

/**
 * Returns true when all required (non-bonus) S12 profile setup areas are
 * complete for the given user. Useful outside the profile page (e.g. on the
 * accreditation tab) to decide whether to show a nudge banner.
 */
export function isS12SetupComplete(user: ExtendedThalamosUser): boolean {
  const areas = buildSetupAreas({
    hasPhoneNumber:
      user.section12PhoneNumber != null && user.section12PhoneNumber.length > 0,
    hasWorkingHours: (user.section12AvailabilityBlocks ?? []).length > 0,
    hasHomeBase: Boolean(
      user.homeBaseAddress?.address && user.homeBaseAddress?.postalCode,
    ),
    hasLaCoverage: (user.section12LocalAuthorityMappings ?? []).length > 0,
    hasSpecialisms: (user.section12Specialisms ?? []).length > 0,
    calendarSyncActive: user.section12CalendarSyncEnabled,
    hasAboutInfo:
      user.section12Gender != null ||
      (user.section12Languages ?? []).length > 0,
  });
  return areas.filter((a) => !a.bonus).every((a) => a.complete);
}

export function SetupScoreCards({
  areas,
  onAreaClick,
  selectedSection,
}: SetupScoreCardsProps) {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);

  const scoredAreas = areas.filter((a) => !a.bonus);
  const completedCount = scoredAreas.filter((a) => a.complete).length;
  const totalCount = scoredAreas.length;
  const percent =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const allComplete = completedCount === totalCount;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        mb: 3,
        borderColor: theme.palette.divider,
      }}
      data-testid="setup-score-cards"
    >
      {/* Progress header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            S12 Profile Setup
          </Typography>
          {isExpanded && (
            <Typography variant="body2" color="text.secondary">
              {allComplete
                ? "Your profile is fully set up — AMHPs can now find you."
                : `Complete the items below so AMHPs can find and contact you.`}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ textAlign: "right" }}>
            <Typography
              variant="h5"
              fontWeight={700}
              color={allComplete ? "success.main" : "primary.main"}
            >
              {percent}%
            </Typography>
            {isExpanded && (
              <Typography
                variant="caption"
                color={allComplete ? "success.main" : "primary.main"}
              >
                {completedCount} of {totalCount} required sections
              </Typography>
            )}
          </Box>
          <ExpandMore
            isExpanded={isExpanded}
            onClick={() => {
              setIsExpanded((prev) => !prev);
            }}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse setup" : "Expand setup"}
            data-testid="setup-score-cards-expand"
          >
            <ExpandMoreIcon sx={{ color: "primary.main" }} />
          </ExpandMore>
        </Box>
      </Box>

      {/* Area cards */}
      <Collapse in={isExpanded}>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            mt: 2,
          }}
        >
          {areas.map((area) => (
            <Box
              key={area.key}
              onClick={() => onAreaClick?.(area.sectionKey)}
              data-testid={`setup-area-${area.key}`}
              data-selected={
                area.sectionKey === selectedSection ? "true" : "false"
              }
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.5,
                py: 0.75,
                borderRadius: "20px",
                border: `2px solid ${
                  area.sectionKey === selectedSection
                    ? theme.palette.primary.main
                    : area.complete
                      ? theme.palette.success.light
                      : theme.palette.grey[300]
                }`,
                backgroundColor:
                  area.sectionKey === selectedSection
                    ? `${theme.palette.primary.main}12`
                    : theme.palette.background.paper,
                cursor: onAreaClick ? "pointer" : "default",
                transition: "all 0.15s ease",
                "&:hover": onAreaClick
                  ? {
                      borderColor:
                        area.sectionKey === selectedSection
                          ? theme.palette.primary.dark
                          : area.complete
                            ? theme.palette.success.main
                            : theme.palette.primary.main,
                      boxShadow: theme.shadows[1],
                    }
                  : {},
                ...(area.bonus && {
                  opacity: 0.75,
                }),
              }}
            >
              <Box
                sx={{
                  color: area.complete
                    ? theme.palette.success.main
                    : theme.palette.grey[400],
                }}
              >
                {area.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  noWrap
                  sx={{
                    color: area.complete
                      ? theme.palette.text.primary
                      : theme.palette.text.secondary,
                  }}
                >
                  {area.label}
                </Typography>
                {area.bonus && (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontSize: "0.65rem" }}
                  >
                    Optional
                  </Typography>
                )}
              </Box>
              {area.complete ? (
                <CheckCircle
                  fontSize="small"
                  sx={{ color: theme.palette.success.main }}
                />
              ) : (
                <RadioButtonUnchecked
                  fontSize="small"
                  sx={{ color: theme.palette.grey[400] }}
                />
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}
